

import { Mail } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import GMark from "@/components/GMark";
import GateFunnel from "@/components/GateFunnel";
import PricingSection from "@/components/PricingSection";
import FooterMeta, { TEAM_EMAIL } from "@/components/FooterMeta";
import { breadcrumb } from "@/lib/schema";

export default function ForGrowers() {
  const emailHref = (() => {
    const to = TEAM_EMAIL;
    const subject = "Grower notes for Gate + Gather";
    const body = [
      "Kia ora Gate + Gather team,",
      "",
      "Here are my answers to your grower questions:",
      "",
      "1. How does your most recent plant sale actually happen — start to finish?",
      "   → ",
      "",
      "2. How often are you asked \"is this still available?\" in a normal week?",
      "   → ",
      "",
      "3. Would you rather show customers exact numbers, \"only 2 left\", or just Available / Sold out?",
      "   → ",
      "",
      "4. What's your split between people who'd pay online now versus at pickup?",
      "   → ",
      "",
      "5. Have you tried Shopify / Wix / Square before? If yes, why did you stop?",
      "   → ",
      "",
      "Thanks,",
      "",
    ].join("\n");
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  })();

  return (
    <div className="min-h-screen bg-white text-[#20251F] print:bg-white">
      <Seo
        title="Sell Plants, Flowers & Seedlings Online NZ | Gate + Gather"
        description="Already selling through Facebook, Instagram, markets or regular customers? Give them one simple Gate to see what's available and order."
        path="/for-growers"
        jsonLd={[
          breadcrumb([
            { name: "Gate + Gather", path: "/" },
            { name: "For growers", path: "/for-growers" },
          ]),
        ]}
      />
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 14mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <div className="max-w-3xl mx-auto px-6 pt-10 print:pt-0">
        <header className="flex items-center justify-between border-b border-[#DED5C3] pb-4">
          <Link to="/" data-testid="growers-nav-brand" className="flex items-center gap-2 group">
            <GMark size={26} className="rounded-md shrink-0 no-print" />
            <span className="font-serif-heading text-lg tracking-tight">Gate<span className="brand-plus text-[1.05em]"> + </span>Gather</span>
          </Link>
          <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#6B6E67]">For growers · One-page walkthrough</p>
        </header>

        <section className="mt-6">
          <h1 className="font-serif-heading text-3xl sm:text-4xl tracking-tight text-[#20251F]">You grow it. Your Gate makes it simple to sell.</h1>
          <p className="mt-3 text-stone-700">Your own storefront for selling what you grow. Show what's available, take orders and keep track of what's sold — without running a complicated ecommerce store.</p>
        </section>

        <section className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-2xl border border-stone-200">
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">1 · Open your Gate</p>
            <p className="font-serif-heading text-lg mt-1">Under 3 minutes</p>
            <p className="text-sm text-stone-700 mt-1">Name your Gate, say where you're based, add your first plant. Then we ask for your email.</p>
          </div>
          <div className="p-4 rounded-2xl border border-stone-200">
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">2 · Add a plant</p>
            <p className="font-serif-heading text-lg mt-1">Under 60 seconds</p>
            <p className="text-sm text-stone-700 mt-1">Photo. Name. Price. Quantity. That's enough to start selling. Everything else is optional.</p>
          </div>
          <div className="p-4 rounded-2xl border border-stone-200">
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">3 · Update stock</p>
            <p className="font-serif-heading text-lg mt-1">Under 5 seconds</p>
            <p className="text-sm text-stone-700 mt-1">Sold four at the market? Tap the − button four times. That's it.</p>
          </div>
          <div className="p-4 rounded-2xl border border-stone-200">
            <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">4 · Share your link</p>
            <p className="font-serif-heading text-lg mt-1">gateandgather.co.nz/g/yourname</p>
            <p className="text-sm text-stone-700 mt-1">Paste it into Facebook, Instagram, your existing customer list or a QR code at your gate.</p>
          </div>
        </section>

        <section className="mt-8">
          <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">What your Gate answers before anyone has to ask</p>
          <ul className="mt-3 grid grid-cols-2 gap-y-1.5 text-sm">
            {["Is this still available?", "How many do you have?", "Where are you?", "When can I collect?", "Do you deliver?", "How do I pay?"].map((q) => (
              <li key={q} className="text-stone-800">✓ <span className="line-through text-stone-500 ml-1">{q}</span></li>
            ))}
          </ul>
        </section>

        {/* GateFunnel — visual explainer showing the four channels a grower
            already uses (FB post, IG story, market QR, text to a regular)
            all funnelling into one Gate, then out to an order. Desktop shows
            the horizontal funnel; mobile stacks the channels vertically. */}
        <GateFunnel />

        <section className="mt-8">
          <p className="font-mono-label text-[10px] uppercase tracking-widest text-[#3F674E]/70">A real order, start to finish</p>
          <ol className="mt-3 space-y-2 text-sm text-stone-800 list-decimal pl-5">
            <li><strong>Customer orders</strong> — Sarah adds 4 × Kōwhai and picks Saturday pickup.</li>
            <li><strong>Stock updates itself</strong> — Kōwhai drops from 6 to 2 available on your Gate.</li>
            <li><strong>You get the order</strong> — Sarah appears on your dashboard with her total and pickup time.</li>
            <li><strong>She pays</strong> — online with a card, or cash/bank/EFTPOS when she collects.</li>
            <li><strong>You mark Collected</strong> — the order moves to your transaction history.</li>
          </ol>
        </section>

        <section className="mt-8 p-5 rounded-2xl bg-[#3F674E]/10 border border-[#3F674E]/20">
          <p className="font-serif-heading text-xl italic text-[#20251F]">"New plants available — shop my Gate."</p>
          <p className="text-sm text-stone-700 mt-2">One link. No back-and-forth. Your Gate. Your customers. Not a marketplace.</p>
          <p className="mt-3 font-mono-label text-xs text-[#3F674E]">Try it: <strong>gateandgather.co.nz</strong> · or see an example at <strong>/g/quail-lane</strong></p>
        </section>

        {/* Pricing — shown BEFORE the reader is asked to sign up. Config-driven
            from /api/plans (backend PLANS dict) so prices and product-allowance
            values stay in sync everywhere. Rendered OUTSIDE the max-w-3xl
            reading column so its 3-column desktop layout has room to breathe
            without squeezing or clipping the CTA pills. */}
      </div>
      <div className="no-print">
        <PricingSection eyebrow="Pricing" heading="Choose your Gate." />
      </div>
      <div className="max-w-3xl mx-auto px-6 pb-10 print:pb-0">

        <section className="mt-8 text-xs text-stone-600 border-t border-stone-200 pt-4">
          <p className="font-medium text-stone-800 mb-1">Questions we'd love your answer to:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>How does your most recent plant sale actually happen — start to finish?</li>
            <li>How often are you asked "is this still available?" in a normal week?</li>
            <li>Would you rather show customers exact numbers, "only 2 left", or just Available / Sold out?</li>
            <li>What's your split between people who'd pay online now versus at pickup?</li>
            <li>Have you tried Shopify / Wix / Square before? If yes, why did you stop?</li>
          </ol>
          <div className="mt-4 no-print flex flex-wrap items-center gap-3">
            <a
              href={emailHref}
              data-testid="grower-email-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#20251F] hover:bg-black text-white h-11 px-5 font-medium text-sm transition-colors"
            >
              <Mail className="w-4 h-4" /> Email us your answers
            </a>
            <p className="text-[11px] text-stone-500 leading-snug">Opens your email app with the questions prefilled. Short answers are welcome — even one is gold.</p>
          </div>
        </section>

        <div className="mt-8 no-print flex justify-center">
          <button onClick={() => window.print()} className="rounded-full bg-[#20251F] hover:bg-black text-white px-6 h-11" data-testid="print-btn">Print this page</button>
        </div>
      </div>

      <footer className="border-t border-[#DED5C3] py-10 mt-6 no-print">
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 flex-wrap">
          <Link to="/" data-testid="growers-footer-brand" className="flex items-center gap-2 group">
            <GMark size={28} className="rounded-md shrink-0" />
            <span className="font-serif-heading text-base tracking-tight">Gate<span className="brand-plus"> + </span>Gather</span>
          </Link>
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/pricing" data-testid="growers-footer-pricing" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Pricing</Link>
            <Link to="/faq" data-testid="growers-footer-faq" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">FAQ</Link>
            <Link to="/policy/seller-policy" data-testid="growers-footer-seller-policy" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Seller Policy</Link>
            <Link to="/policy/privacy" data-testid="growers-footer-privacy" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Privacy</Link>
            <Link to="/policy/terms" data-testid="growers-footer-terms" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Terms</Link>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 mt-4">
          <FooterMeta />
        </div>
      </footer>
    </div>
  );
}
